import { Link, useNavigate } from "react-router-dom";
import { useDashboardOverview, useTerritorialIndicatorsSummary } from "@/hooks";
import PageHeader from "@/components/shared/PageHeader";
import KpiCard from "@/components/shared/KpiCard";
import ConditionCard, { type Segment } from "./components/ConditionCard";
import ActivityChart from "./components/ActivityChart";
import AttentionList from "./components/AttentionList";
import RecentInspections from "./components/RecentInspections";
import { ProjectsCard, RegistrationOrigin, TopSpecies } from "./components/SideCards";
import { healthStyle, riskStyle } from "./labels";

type Tone = "ok" | "warn" | "danger";

const TONE_STYLE: Record<Tone, { box: string; icon: string }> = {
  ok: { box: "bg-primary-50/60 border-primary-200/70", icon: "bg-primary-100 text-primary-700" },
  warn: { box: "bg-warning-50/60 border-warning-200/70", icon: "bg-warning-100 text-warning-700" },
  danger: { box: "bg-danger-50/60 border-danger-200/70", icon: "bg-danger-100 text-danger-700" },
};

function AttentionTile({
  label,
  count,
  hint,
  icon,
  tone,
  to,
}: {
  label: string;
  count: number;
  hint: string;
  icon: string;
  tone: Tone;
  to: string;
}) {
  const style = TONE_STYLE[count === 0 ? "ok" : tone];
  return (
    <Link
      to={to}
      className={`flex items-center gap-4 rounded-card border p-4 md:p-5 transition-shadow hover:shadow-sm ${style.box}`}
    >
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${style.icon}`}>
        <i className={`${count === 0 ? "ri-checkbox-circle-line" : icon} text-xl`} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-foreground-900 font-heading tabular-nums leading-none">{count.toLocaleString("pt-BR")}</p>
        <p className="text-sm font-medium text-foreground-800 mt-1">{label}</p>
        <p className="text-xs text-foreground-500">{count === 0 ? "Tudo em dia" : hint}</p>
      </div>
      <i className="ri-arrow-right-s-line text-xl text-foreground-400 ml-auto" />
    </Link>
  );
}

function Skeleton() {
  return (
    <div className="space-y-6 animate-pulse" aria-busy="true" aria-label="Carregando painel">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 rounded-card bg-background-100" />
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-24 rounded-card bg-background-100" />
        ))}
      </div>
      <div className="h-64 rounded-card bg-background-100" />
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { data, loading, error } = useDashboardOverview();
  const { data: territorial } = useTerritorialIndicatorsSummary();

  const header = (
    <PageHeader
      title="Dashboard FloraIQ"
      subtitle={`Situação do cadastro técnico arbóreo da sua organização · ${new Date().toLocaleDateString("pt-BR")}`}
    >
      <button onClick={() => navigate("/projetos/novo")} className="btn-outline text-xs px-3 py-2 cursor-pointer">
        <i className="ri-folder-add-line" />
        <span className="hidden sm:inline">Novo projeto</span>
      </button>
      <button onClick={() => navigate("/cta/novo")} className="btn-primary text-xs px-3 py-2 cursor-pointer">
        <i className="ri-add-line" />
        <span className="hidden sm:inline">Cadastrar exemplar</span>
      </button>
    </PageHeader>
  );

  if (loading) {
    return (
      <div>
        {header}
        <Skeleton />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        {header}
        <div className="rounded-card border border-danger-200 bg-danger-50/60 p-6 text-sm text-danger-800 flex items-start gap-3">
          <i className="ri-error-warning-line text-xl" />
          <div>
            <p className="font-medium">Não foi possível carregar o painel.</p>
            <p className="text-danger-700/80 mt-0.5">{error ?? "Resposta vazia do servidor."} Tente recarregar a página.</p>
          </div>
        </div>
      </div>
    );
  }

  const toSegments = (rows: Array<{ label: string; count: number }>, style: typeof healthStyle): Segment[] =>
    rows.map((r) => ({ key: r.label, text: style(r.label).text, color: style(r.label).color, count: r.count }));

  const highRisk = data.riskDistribution
    .filter((r) => r.label === "ALTO" || r.label === "CRITICO")
    .reduce((sum, r) => sum + r.count, 0);

  const territorialInspections = territorial
    ? territorial.avgTreeCoverageByNeighborhood.reduce((sum, r) => sum + r.totalInspections, 0)
    : null;

  return (
    <div className="space-y-6">
      {header}

      {/* O que pede uma ação agora */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <AttentionTile
          label="Vistorias aguardando revisão"
          count={data.pendingReview}
          hint="Enviadas ou em revisão"
          icon="ri-file-search-line"
          tone="warn"
          to="/vistorias"
        />
        <AttentionTile
          label="Exemplares com risco alto ou crítico"
          count={highRisk}
          hint="Conforme a última vistoria validada"
          icon="ri-alert-line"
          tone="danger"
          to="/cta"
        />
        <AttentionTile
          label="Exemplares sem avaliação validada"
          count={data.treesWithoutValidatedInspection}
          hint="Sem saúde nem risco definidos"
          icon="ri-question-line"
          tone="warn"
          to="/cta"
        />
      </div>

      {/* Números do inventário */}
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
        <KpiCard label="Exemplares" value={data.totalTrees} icon="ri-plant-line" variant="success" onClick={() => navigate("/cta")} />
        <KpiCard label="Vistorias" value={data.totalInspections} icon="ri-clipboard-line" variant="info" onClick={() => navigate("/vistorias")} />
        <KpiCard
          label="Projetos ativos"
          value={`${data.activeProjects} de ${data.totalProjects}`}
          icon="ri-folder-chart-line"
          variant="info"
          onClick={() => navigate("/projetos")}
        />
        <KpiCard label="Espécies em uso" value={data.totalSpeciesInUse} icon="ri-seedling-line" />
        <KpiCard label="Campanhas em andamento" value={data.activeCampaigns} icon="ri-route-line" onClick={() => navigate("/campanhas")} />
        <KpiCard
          label="Quadras analisadas"
          value={territorialInspections ?? "—"}
          icon="ri-map-2-line"
          variant="success"
          onClick={() => navigate("/territorio/inspecoes")}
        />
      </div>

      {/* Condição e atividade */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <ConditionCard
            health={toSegments(data.healthDistribution, healthStyle)}
            risk={toSegments(data.riskDistribution, riskStyle)}
            withoutAssessment={data.treesWithoutValidatedInspection}
            totalTrees={data.totalTrees}
          />
          <ActivityChart data={data.monthlyActivity} />
        </div>
        <div className="space-y-6">
          <AttentionList items={data.attentionTrees} />
          <TopSpecies rows={data.topSpecies} />
        </div>
      </div>

      {/* Detalhe operacional */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <RecentInspections items={data.recentInspections} />
        </div>
        <div className="space-y-6">
          <ProjectsCard projects={data.projects} />
          <RegistrationOrigin rows={data.registrationOrigin} />
        </div>
      </div>
    </div>
  );
}
